// Comprehensive error monitoring and prevention system
// Note: Toast notifications are handled by components that use this library
import { DRXError, toDRXError } from './drx-error';
import { captureErrorToSentry } from './sentry';
import { isStaleAccountError } from './stale-account';

// Error types we want to monitor
export enum ErrorType {
  FIRESTORE_INVALID_DATA = 'firestoreInvalidData',
  CART_SYNC_ERROR = 'cartSyncError',
  UNDEFINED_FIELD_ERROR = 'undefinedFieldError',
  BROWSER_EXTENSION_CONFLICT = 'browserExtensionConflict',
  CONSOLE_SPAM = 'consoleSpam',
  AUTHENTICATION_ERROR = 'authError',
  NETWORK_ERROR = 'networkError'
}

// Error patterns to detect
const ERROR_PATTERNS = {
  [ErrorType.FIRESTORE_INVALID_DATA]: /Function updateDoc\(\) called with invalid data.*Unsupported field value: undefined/,
  [ErrorType.CART_SYNC_ERROR]: /Error syncing cart/,
  [ErrorType.UNDEFINED_FIELD_ERROR]: /Cannot read properties of undefined|Minified React error #306/,
  [ErrorType.BROWSER_EXTENSION_CONFLICT]: /(zotero|singlefile|messagingInject).*already been declared/,
  [ErrorType.CONSOLE_SPAM]: /Loaded guest cart from localStorage: Array\(0\)/,
  [ErrorType.AUTHENTICATION_ERROR]: /Failed to get Firebase project/,
  [ErrorType.NETWORK_ERROR]: /NetworkError|Failed to fetch/
};

const ERROR_TYPE_DEFAULTS: Record<
  ErrorType,
  { code: string; userMessage: string; level: DRXError['level']; capture: boolean }
> = {
  [ErrorType.FIRESTORE_INVALID_DATA]: {
    code: 'FIRESTORE_INVALID_DATA',
    userMessage: 'We could not save your changes. Please refresh and try again.',
    level: 'error',
    capture: true,
  },
  [ErrorType.CART_SYNC_ERROR]: {
    code: 'CART_SYNC_ERROR',
    userMessage: "Your cart couldn't sync right now. Please try again.",
    level: 'error',
    capture: true,
  },
  [ErrorType.UNDEFINED_FIELD_ERROR]: {
    code: 'UNDEFINED_FIELD_ERROR',
    userMessage: 'Something went wrong while loading this view.',
    level: 'error',
    capture: true,
  },
  [ErrorType.BROWSER_EXTENSION_CONFLICT]: {
    code: 'BROWSER_EXTENSION_CONFLICT',
    userMessage: 'A browser extension interfered with this page.',
    level: 'warning',
    capture: false,
  },
  [ErrorType.CONSOLE_SPAM]: {
    code: 'CONSOLE_SPAM',
    userMessage: 'No action needed.',
    level: 'info',
    capture: false,
  },
  [ErrorType.AUTHENTICATION_ERROR]: {
    code: 'AUTHENTICATION_ERROR',
    userMessage: 'Your session could not be verified. Please sign in again.',
    level: 'error',
    capture: true,
  },
  [ErrorType.NETWORK_ERROR]: {
    code: 'NETWORK_ERROR',
    userMessage: 'Network issue detected. Please check your connection and retry.',
    level: 'error',
    capture: true,
  },
};

// Error counter to prevent spam
const errorCounts = new Map<string, number>();
const ERROR_THRESHOLD = 3;
const RESET_INTERVAL = 60000; // 1 minute

// Reset error counts periodically
setInterval(() => {
  errorCounts.clear();
}, RESET_INTERVAL);

// Data validation utilities
export const validateFirestoreData = (data: any): boolean => {
  if (!data || typeof data !== 'object') return false;
  
  return !hasUndefinedValues(data);
};

export const hasUndefinedValues = (obj: any): boolean => {
  if (obj === undefined) return true;
  if (obj === null) return false;
  if (typeof obj !== 'object') return false;
  
  for (const key in obj) {
    if (obj[key] === undefined) return true;
    if (typeof obj[key] === 'object' && hasUndefinedValues(obj[key])) return true;
  }
  return false;
};

// Clean object by removing undefined values
export const cleanObject = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const cleaned: Partial<T> = {};
  
  for (const key in obj) {
    const value = obj[key];
    if (value !== undefined) {
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const cleanedNested = cleanObject(value);
        if (Object.keys(cleanedNested).length > 0) {
          cleaned[key] = cleanedNested as T[Extract<keyof T, string>];
        }
      } else {
        cleaned[key] = value;
      }
    }
  }
  
  return cleaned;
};

// Cart item validation
export const validateCartItem = (item: any): boolean => {
  if (!item || typeof item !== 'object') return false;
  
  // Required fields - check for various ID field names
  const itemId = item.resourceId || item.itemId || item.id;
  if (!itemId) return false;
  
  // Quantity validation
  const quantity = Number(item.quantity);
  if (!quantity || quantity <= 0) return false;
  
  // Optional fields should not be undefined (but can be missing)
  if (item.title !== undefined && typeof item.title !== 'string') return false;
  if (item.price !== undefined && typeof item.price !== 'number' && typeof item.price !== 'string') return false;
  if (item.imageUrl !== undefined && typeof item.imageUrl !== 'string') return false;
  
  return true;
};

// Enhanced error handler with DRXError + Sentry pipeline
export const handleError = (
  error: unknown,
  context?: string,
  toastFn?: any
): DRXError => {
  const rawMessage = (() => {
    if (typeof error === 'string') return error;
    if (error instanceof Error) return error.message;
    return String(error);
  })();

  const matchedErrorType = (() => {
    for (const [type, pattern] of Object.entries(ERROR_PATTERNS)) {
      if (pattern.test(rawMessage)) {
        return type as ErrorType;
      }
    }
    return null;
  })();

  const defaults = matchedErrorType ? ERROR_TYPE_DEFAULTS[matchedErrorType] : undefined;

  const drxError = toDRXError(error, {
    code: defaults?.code || 'UNEXPECTED_ERROR',
    userMessage: defaults?.userMessage,
    level: defaults?.level || 'error',
    context: {
      context: context || 'unknown',
      rawMessage,
      errorType: matchedErrorType || 'UNCLASSIFIED',
    },
  });

  if (shouldIgnoreError(drxError.devMessage)) {
    return drxError;
  }

  const errorKey = `${context || 'unknown'}:${drxError.code}:${drxError.devMessage}`;
  const count = errorCounts.get(errorKey) || 0;
  errorCounts.set(errorKey, count + 1);

  if (count >= ERROR_THRESHOLD) {
    return drxError;
  }

  // Skip noisy/expected telemetry from browser extensions.
  if (matchedErrorType === ErrorType.BROWSER_EXTENSION_CONFLICT) {
    console.warn(`[${drxError.code}] ${drxError.devMessage}`);
    return drxError;
  }

  // Skip low-value spam.
  if (matchedErrorType === ErrorType.CONSOLE_SPAM) {
    return drxError;
  }

  if (defaults?.capture !== false) {
    captureErrorToSentry(drxError, {
      context,
      extra: {
        occurrenceCount: count + 1,
      },
    });
  }

  const logFn = drxError.level === 'warning' || drxError.level === 'info' ? console.warn : console.error;
  logFn(`[${drxError.code}] ${drxError.devMessage}`, {
    context,
    userMessage: drxError.userMessage,
  });

  if (toastFn && count === 0) {
    toastFn({
      title: drxError.code.replace(/_/g, ' '),
      description: drxError.userMessage,
      variant: drxError.level === 'warning' || drxError.level === 'info' ? 'default' : 'destructive',
    });
  }

  return drxError;
};

// Helper to check if error is from Stripe analytics (blocked by ad blocker)
const isStripeAnalyticsError = (error: any): boolean => {
  if (!error) return false;

  const errorMessage = error.message?.toLowerCase() || '';
  const errorName = error.name?.toLowerCase() || '';
  const stack = error.stack?.toLowerCase() || '';

  return (
    (errorMessage.includes('fetch') || errorMessage.includes('errBlockedByClient')) &&
    (stack.includes('stripe') || errorMessage.includes('stripe.com/b'))
  );
};

declare global {
  interface Window {
    __drxGlobalErrorHandlingInstalled__?: boolean;
    __drxConsoleBridgeInstalled__?: boolean;
    __drxOriginalConsoleError__?: typeof console.error;
  }
}

const formatConsoleArg = (arg: unknown): string => {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
};

const findErrorLikeArg = (args: unknown[]): Error | null => {
  for (const arg of args) {
    if (arg instanceof Error) return arg;
  }
  return null;
};

// Global error handler setup
export const setupGlobalErrorHandling = (): void => {
  if (window.__drxGlobalErrorHandlingInstalled__) {
    return;
  }
  window.__drxGlobalErrorHandlingInstalled__ = true;

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (isStripeAnalyticsError(event.reason)) {
      event.preventDefault();
      console.info('Stripe analytics blocked by ad blocker (normal; does not affect payments)');
      return;
    }

    handleError(event.reason, 'unhandledPromise');
    event.preventDefault();
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    if (event.filename?.includes('chrome-extension://')) {
      return;
    }

    if (isStripeAnalyticsError(event.error)) {
      event.preventDefault();
      console.info('Stripe analytics blocked by ad blocker (normal; does not affect payments)');
      return;
    }

    handleError(event.error || event.message, 'globalError');
    event.preventDefault();
  });

  // Capture errors that are only logged via console.error (without throwing).
  if (!window.__drxConsoleBridgeInstalled__) {
    window.__drxConsoleBridgeInstalled__ = true;
    const originalConsoleError = console.error;
    window.__drxOriginalConsoleError__ = originalConsoleError;

    console.error = (...args: any[]) => {
      // Requests for the account that was just signed out are refused;
      // that's expected, so it's a warning (and never sent to Sentry).
      if (args.some((a) => isStaleAccountError(a))) {
        console.warn('(after an account change)', ...args);
        return;
      }
      originalConsoleError.apply(console, args);

      const combinedMessage = args.map(formatConsoleArg).join(' ').trim();
      if (!combinedMessage) return;

      // Avoid recapturing DRX logs and known ignored noise.
      if (combinedMessage.startsWith('[') || shouldIgnoreError(combinedMessage)) {
        return;
      }

      const errorLikeArg = findErrorLikeArg(args);

      // Also check the error message of Error objects against ignore list
      if (errorLikeArg && shouldIgnoreError(errorLikeArg.message)) {
        return;
      }

      const shouldCaptureConsoleBridge =
        !!errorLikeArg ||
        /Failed to load component|ErrorBoundary caught an error|Unhandled/i.test(combinedMessage);

      if (!shouldCaptureConsoleBridge) {
        return;
      }

      const consoleBridgeError = new DRXError({
        code: 'CONSOLE_ERROR',
        devMessage: combinedMessage,
        userMessage: 'An unexpected error occurred.',
        level: 'error',
        tags: { source: 'consoleErrorBridge' },
        cause: errorLikeArg || combinedMessage,
      });

      captureErrorToSentry(consoleBridgeError, {
        context: 'consoleErrorBridge',
      });
    };
  }
};

// Firestore operation wrapper with validation
export const safeFirestoreUpdate = async (
  operation: () => Promise<void>,
  data: any,
  context: string
): Promise<boolean> => {
  try {
    // Validate data before operation
    if (!validateFirestoreData(data)) {
      console.error('🔥 Invalid Firestore data detected:', data);
      handleError('Invalid data structure detected before Firestore operation', context);
      return false;
    }
    
    await operation();
    return true;
  } catch (error) {
    handleError(error as Error, context);
    return false;
  }
};

export const runAsyncSafely = async <T>(
  operation: () => Promise<T>,
  context: string,
  fallbackValue?: T
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    handleError(error, context);
    if (fallbackValue !== undefined) {
      return fallbackValue;
    }
    throw error;
  }
};

// Development mode debugging
export const debugMode = import.meta.env.DEV;

export const debugLog = (message: string, data?: any): void => {
  if (debugMode) {
    console.log(`🐛 [DEBUG] ${message}`, data || '');
  }
};

// Performance monitoring for repeated operations
const performanceCounters = new Map<string, { count: number, totalTime: number }>();

export const measurePerformance = <T>(
  operation: () => T,
  operationName: string
): T => {
  const start = performance.now();
  const result = operation();
  const end = performance.now();
  const duration = end - start;
  
  const counter = performanceCounters.get(operationName) || { count: 0, totalTime: 0 };
  counter.count++;
  counter.totalTime += duration;
  performanceCounters.set(operationName, counter);
  
  // Warn about slow operations
  if (duration > 100) {
    console.warn(`⚠️ Slow operation detected: ${operationName} took ${duration.toFixed(2)}ms`);
  }
  
  // Log performance stats every 50 operations
  if (counter.count % 50 === 0) {
    const avgTime = counter.totalTime / counter.count;
    debugLog(`Performance stats for ${operationName}: ${counter.count} calls, avg ${avgTime.toFixed(2)}ms`);
  }
  
  return result;
};

// Safe array access to prevent React Error #306
export const safeArrayAccess = <T>(
  array: T[] | undefined | null,
  defaultValue: T[] = []
): T[] => {
  if (!array || !Array.isArray(array)) {
    debugLog('safeArrayAccess: Invalid array provided, returning default', { array, defaultValue });
    return defaultValue;
  }
  return array;
};

// Safe length access to prevent undefined.length errors
export const safeLength = (
  array: any[] | undefined | null
): number => {
  if (!array || !Array.isArray(array)) {
    debugLog('safeLength: Invalid array provided, returning 0', { array });
    return 0;
  }
  return array.length;
};

// Safe array operation wrapper
export const withSafeArray = <T, R>(
  array: T[] | undefined | null,
  operation: (safeArray: T[]) => R,
  defaultResult: R
): R => {
  const safeArray = safeArrayAccess(array);
  try {
    return operation(safeArray);
  } catch (error) {
    handleError(error as Error, 'safe-array-operation');
    return defaultResult;
  }
};

// Export utility for checking if error should be ignored
export const shouldIgnoreError = (error: string): boolean => {
  const ignoredPatterns = [
    /chrome-extension:/,
    /zotero/i,
    /singlefile/i,
    /Identifier.*already been declared/,
    /pageSaving\.js/,
    /messagingInject\.js/,
    /must be used within a.*Provider/i,  // React context errors (including auth)
    /FirebaseAuthProvider/i  // Firebase auth context errors
  ];

  return ignoredPatterns.some(pattern => pattern.test(error));
};
